import { useEffect, useState } from 'react';
import TileCard from '../TileCard';
import { getWorkflowRunsSummary, prefetch } from '../../api/client';
import type { Env, WorkflowRunsSummary } from '../../types';

const HELP =
  'Count of Logic App workflow runs in the selected environment over the last 24 hours, ' +
  'broken down by outcome. Green = Succeeded, red = Failed, amber = still running. ' +
  'If Failed is above zero, open the Workflow detail page to see which workflows failed ' +
  'and which actions are slow.';

export default function WorkflowRunsSummaryTile({ env, detailsHref }: { env: Env; detailsHref?: string }) {
  const [data, setData] = useState<WorkflowRunsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getWorkflowRunsSummary(env)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Warm up the detail endpoints so the detail page (which opens in a new
    // tab) can render from cache instantly. 24h is the default window.
    prefetch('workflow-runs-breakdown', { env, windowHours: 24 });
    prefetch('workflow-durations', { env, windowHours: 24 });
    return () => {
      cancelled = true;
    };
  }, [env]);

  const generatedFooter = data ? (
    <span className="tile-footer-row">
      <span>As of {new Date(data.generatedAt).toLocaleTimeString()} · 24h window</span>
      {detailsHref && (
        <a href={`#${detailsHref}`} className="tile-details-link" target="_blank" rel="noopener noreferrer">
          View details →
        </a>
      )}
    </span>
  ) : undefined;

  return (
    <TileCard
      title={`Workflow runs — last 24h (${env.toUpperCase()})`}
      help={HELP}
      hideHelp={!!detailsHref}
      loading={loading}
      error={error}
      footer={generatedFooter}
    >
      {data?.status === 'not-configured' && (
        <p className="tile-empty">{data.message}</p>
      )}
      {data?.status === 'error' && (
        <div className="tile-error">Query failed: {data.message}</div>
      )}
      {data?.status === 'ok' && (
        <dl className="stats-grid">
          <div>
            <dt>Total</dt>
            <dd>{data.total ?? 0}</dd>
          </div>
          <div className="ok">
            <dt>Succeeded</dt>
            <dd>{data.succeeded ?? 0}</dd>
          </div>
          <div className="warn">
            <dt>Running</dt>
            <dd>{data.running ?? 0}</dd>
          </div>
          <div className={(data.failed ?? 0) > 0 ? 'bad' : 'ok'}>
            <dt>Failed</dt>
            <dd>{data.failed ?? 0}</dd>
          </div>
        </dl>
      )}
    </TileCard>
  );
}
